import { useRef, useState, type KeyboardEvent } from "react";
import { Input } from "@/components/ui/input";
import { SrIcon } from "@/components/ui/SrIcon";
import { useBrregSearch, type BrregHit } from "@/hooks/use-brreg-search";

interface BrregCompanyNameFieldProps {
  name: string;
  onNameChange: (name: string) => void;
  onHitSelect: (hit: BrregHit) => void;
}

/**
 * Firmanavn-felt med Brønnøysund-combobox. Kun for norske kunder —
 * utenlands-flyten bruker et vanlig Input uten søk.
 */
export function BrregCompanyNameField({
  name,
  onNameChange,
  onHitSelect,
}: BrregCompanyNameFieldProps) {
  const [brregOpen, setBrregOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const blurTimeoutRef = useRef<number | null>(null);
  const { data: brregHits, isFetching: brregSearching } = useBrregSearch(name);

  const applyHit = (hit: BrregHit) => {
    onHitSelect(hit);
    setBrregOpen(false);
    setHighlightedIndex(0);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!brregOpen || !brregHits || brregHits.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlightedIndex((h) => Math.min(h + 1, brregHits.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlightedIndex((h) => Math.max(h - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const hit = brregHits[highlightedIndex];
      if (hit) applyHit(hit);
    } else if (e.key === "Escape") {
      e.preventDefault();
      setBrregOpen(false);
    }
  };

  const handleBlur = () => {
    blurTimeoutRef.current = window.setTimeout(() => setBrregOpen(false), 120);
  };

  const handleFocus = () => {
    if (blurTimeoutRef.current !== null) {
      window.clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    setBrregOpen(true);
  };

  const showDropdown =
    brregOpen && name.trim().length >= 2 && (brregHits?.length ?? 0) > 0;

  return (
    <div className="relative">
      <Input
        value={name}
        onChange={(e) => {
          onNameChange(e.target.value);
          setHighlightedIndex(0);
          setBrregOpen(true);
        }}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        placeholder="Skriv firmanavn for å søke i Brønnøysund"
        autoComplete="off"
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
      />
      {brregSearching && (
        <SrIcon
          name="loader2"
          className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground"
        />
      )}
      {showDropdown && (
        <ul
          role="listbox"
          className="absolute z-50 mt-1 w-full max-h-72 overflow-y-auto rounded-md border border-border bg-popover shadow-lg"
        >
          {brregHits!.map((hit, i) => {
            const active = highlightedIndex === i;
            return (
              <li key={hit.organisasjonsnummer}>
                <button
                  type="button"
                  role="option"
                  aria-selected={active}
                  onMouseEnter={() => setHighlightedIndex(i)}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    applyHit(hit);
                  }}
                  className={
                    "w-full text-left px-3 py-2 text-sm flex flex-col gap-0.5 " +
                    (active ? "bg-muted/60" : "hover:bg-muted/60")
                  }
                >
                  <span className="font-medium">{hit.navn}</span>
                  <span className="text-xs text-muted-foreground">
                    {hit.organisasjonsnummer}
                    {hit.poststed ? ` · ${hit.poststed}` : ""}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
